import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';
import { formatErrorMessage } from '../utils/helpers';

const AuthContext = createContext();

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState({
    user: null,
    token: null,
    loading: true,
    error: null,
    isAuthenticated: false,
  });

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = authService.getToken();
      const user = authService.getCurrentUser();

      if (token && user) {
        try {
          // Validate token by fetching user profile
          const profile = await authService.getProfile();
          setState({
            user: profile.user || profile,
            token,
            loading: false,
            error: null,
            isAuthenticated: true,
          });
        } catch (error) {
          // Token is invalid or expired
          authService.logout();
          setState({
            user: null,
            token: null,
            loading: false,
            error: null,
            isAuthenticated: false,
          });
        }
      } else {
        setState((prev) => ({
          ...prev,
          loading: false,
        }));
      }
    };

    initAuth();
  }, []);

  /**
   * Register new user
   * @param {object} userData - User registration data
   * @returns {Promise} - Response data
   */
  const registerUser = async (userData) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await authService.register(userData);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: null,
      }));
      return response;
    } catch (error) {
      const errorMessage = formatErrorMessage(error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
      throw error;
    }
  };

  /**
   * Login user
   * @param {object} credentials - Email and password
   * @returns {Promise} - Response data with user and token
   */
  const loginUser = async (credentials) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await authService.login(credentials);

      setState({
        user: response.user,
        token: response.token,
        loading: false,
        error: null,
        isAuthenticated: true,
      });

      return response;
    } catch (error) {
      const errorMessage = formatErrorMessage(error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
        isAuthenticated: false,
      }));
      throw error;
    }
  };

  /**
   * Logout user
   * Clears auth state and redirects to login
   */
  const logoutUser = () => {
    authService.logout();
    setState({
      user: null,
      token: null,
      loading: false,
      error: null,
      isAuthenticated: false,
    });
  };

  /**
   * Load user profile
   * @returns {Promise} - User profile data
   */
  const loadUser = async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const token = authService.getToken();

      if (!token) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: 'No token found',
        }));
        return;
      }

      const profile = await authService.getProfile();

      setState({
        user: profile.user || profile,
        token,
        loading: false,
        error: null,
        isAuthenticated: true,
      });

      return profile;
    } catch (error) {
      const errorMessage = formatErrorMessage(error);
      authService.logout();
      setState({
        user: null,
        token: null,
        loading: false,
        error: errorMessage,
        isAuthenticated: false,
      });
      throw error;
    }
  };

  /**
   * Update user profile
   * @param {object} updates - Profile updates
   * @returns {Promise} - Updated user data
   */
  const updateUser = async (updates) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await authService.updateProfile(updates);
      const updatedUser = response.user || response;

      setState((prev) => ({
        ...prev,
        user: updatedUser,
        loading: false,
        error: null,
      }));

      return updatedUser;
    } catch (error) {
      const errorMessage = formatErrorMessage(error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
      throw error;
    }
  };

  /**
   * Clear error message
   */
  const clearError = () => {
    setState((prev) => ({ ...prev, error: null }));
  };

  const value = {
    // State
    user: state.user,
    token: state.token,
    loading: state.loading,
    error: state.error,
    isAuthenticated: state.isAuthenticated,

    // Actions
    registerUser,
    loginUser,
    logoutUser,
    loadUser,
    updateUser,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
